import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { FilterModel } from 'ag-grid-community';
import { patchState } from '../../nested-value-getter';
import { OrmService } from '../../schema/orm.service';
import { TransactionFirestoreService } from '../../services/transaction.firestore.service';
import { UserState } from '../user/user.state';
import { TransactionActions } from './transaction.action';
import { TransactionStateModel } from './transaction.store';
import { FirebaseError } from 'firebase/app';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MissingFirebaseIndexComponent } from '@res/ng-notification';

@State<TransactionStateModel>({
  name: 'transactions',
  defaults: {
    transactions: [],
    isSubscribed: false,
    isLoading: false,
    sort: [],
  },
})
@Injectable()
export class TransactionState {
  private transactionService = inject(TransactionFirestoreService);
  private dbUser = inject(OrmService).userCollection;
  private store = inject(Store);
  private snackBar = inject(MatSnackBar);

  @Action(TransactionActions.GetAllTransaction)
  async getAllTransactions(ctx: StateContext<TransactionStateModel>) {
    ctx.patchState({ isLoading: true });
    const transactions = await this.transactionService.GetAll();
    ctx.patchState({
      transactions: transactions.map(({ data, ref }) => ({ ...data, ref })),
      isLoading: false,
    });
  }

  @Action(TransactionActions.UpdateTransaction)
  async updateTransaction(
    ctx: StateContext<TransactionStateModel>,
    { transactionData, ref }: TransactionActions.UpdateTransaction,
  ) {
    await this.transactionService.Update(ref, transactionData);
  }

  @Action(TransactionActions.PaginateQueryTransaction)
  async queryTransaction(
    ctx: StateContext<TransactionStateModel>,
    { request, isResetAfterFilterOrSort, userId }: TransactionActions.PaginateQueryTransaction,
  ) {
    try {
      const state = ctx.getState();
      if (request.filterModel && (request.filterModel as FilterModel)['userId']) {
        (request.filterModel as FilterModel)['userId']['values'] = (request.filterModel as FilterModel)[
          'userId'
        ].values.map(
          (v: string) => this.store.selectSnapshot(UserState.users).find((user) => user.companyName === v)?.ref,
        );
      }
      if (userId) {
        request.filterModel = {
          userId: { filter: this.dbUser.ref(this.dbUser.id(userId)), type: 'equals' },
          ...request.filterModel,
        };
      }

      ctx.patchState({ isLoading: true });
      const { runs, direction } = this.transactionService.SetQuery({
        ...request,
        count: state.transactions.length,
        data: state.transactions,
      });
      if (isResetAfterFilterOrSort) {
        const trCount = state.transactions.length;
        state.transactions = [];
        state.transactions.length = trCount;

        // const array: TransactionStoreModel[] = [];
        // array.length = state.transactions.length;
        // ctx.patchState({ transactions: array });
      }
      if (JSON.stringify(state.filter) !== JSON.stringify(request.filterModel)) {
        ctx.patchState({ filter: request?.filterModel });
        state.transactions = [];
        state.transactions.length = await this.transactionService.GetBuildedCount(runs);

        // const array: Array<TransactionStoreModel> = [];
        // array.length = await this.transactionService.GetBuildedCount(runs);
        // ctx.patchState({ transactions: array });
      }
      if (state.transactions.length !== 0) {
        runs.push(...this.transactionService.SetLimit({ ...request, count: state.transactions.length, data: [] }));
        ctx.patchState({ sort: request?.sortModel });

        if (!runs.some((x) => x.type === 'limit')) {
          throw new Error('No limit');
        }
        const transactions = await this.transactionService.GetBuildedData(runs);
        ctx.patchState({ filter: request?.filterModel });

        if (direction === 'asc') {
          transactions.reverse().forEach(
            ({ data, ref }, i) =>
              (state.transactions[i + (request?.startRow ?? 0)] = {
                ...data,
                ref,
              }),
          );
        } else {
          transactions.forEach(
            ({ data, ref }, i) =>
              (state.transactions[i + (request?.startRow ?? 0)] = {
                ...data,
                ref,
              }),
          );
        }
      }

      ctx.patchState({ isLoading: false, transactions: state.transactions });
    } catch (error) {
      console.error(error);
      ctx.dispatch(new TransactionActions.TransactionError(error as Error));
    }
  }

  @Action(TransactionActions.TransactionError)
  transactionError(ctx: StateContext<TransactionStateModel>, { error }: TransactionActions.TransactionError) {
    if (error instanceof FirebaseError) {
      this.snackBar.openFromComponent(MissingFirebaseIndexComponent, {
        data: error,
        announcementMessage: 'Missing Firebase Index',
      });
    }
    console.error({ error });
    // await this.transactionService.Update(id, transactionData);
  }

  @Action(TransactionActions.DeleteTransactions)
  async deleteTransactions(ctx: StateContext<TransactionStateModel>, { refs }: TransactionActions.DeleteTransactions) {
    await this.transactionService.BatchDelete(refs);
  }

  @Action(TransactionActions.GetSumsCountOf)
  public async getCountOf(
    ctx: StateContext<TransactionStateModel>,
    { fields, timePeriod, filterModel, userId }: TransactionActions.GetSumsCountOf,
  ) {
    const query = {
      filterModel: {},
      startRow: undefined,
      endRow: undefined,
      sortModel: [],
      data: [],
      count: 0,
    };
    if (timePeriod.dateFrom && timePeriod.dateTo) {
      (query.filterModel as FilterModel)['createdAt'] = {
        colId: 'createdAt',
        type: 'inRange',
        filter: timePeriod.dateFrom,
        filterTo: timePeriod.dateTo,
      };
    }
    if (userId) {
      filterModel = { userId: { filter: this.dbUser.ref(this.dbUser.id(userId)), type: 'equals' }, ...filterModel };
    }
    for (const [fieldKey, fieldFilter] of Object.entries(filterModel ?? {})) {
      (query.filterModel as FilterModel)[fieldKey] = fieldFilter;
    }
    (query.filterModel as FilterModel)['paymentResult'] = {
      colId: 'paymentResult',
      type: 'equals',
      filter: 'Successful',
    };

    const queries: { [key: string]: { sum: number; count: number } } = {};
    for (const field of fields) {
      const setQueryArr = field.sumId.map((id) => ({
        ...this.transactionService.SetQuery({
          ...query,
          filterModel: {
            ...query.filterModel,
            [id['conditionKey']]: {
              type: 'equals',
              filter: id['conditionValue'],
            },
          },
        }),
        fieldIdParams: id['targetParam'],
      }));
      queries[field.name] = {
        sum: (
          await Promise.all(
            setQueryArr.map(async (q) => await this.transactionService.QuerySumByCreationDate(q.fieldIdParams, q.runs)),
          )
        ).reduce((acc, val) => acc + val, 0),
        count: (
          await Promise.all(
            setQueryArr.map(
              async (q) =>
                await this.transactionService.QueryCountByCreationDate(field.countId, field.countValue, q.runs),
            ),
          )
        ).reduce((acc, val) => acc + val, 0),
      };
    }
    ctx.patchState({ sums: queries });
  }

  @Action(TransactionActions.UpdateTransactionResult)
  updateTransactionResult(
    ctx: StateContext<TransactionStateModel>,
    { data }: TransactionActions.UpdateTransactionResult,
  ) {
    patchState<TransactionStateModel>(ctx, 'transactions', data);
  }

  @Selector()
  static transactions(state: TransactionStateModel) {
    return state.transactions;
  }
  @Selector()
  static transactionsCount(state: TransactionStateModel) {
    return state.transactions.length;
  }

  @Selector()
  static sums(state: TransactionStateModel) {
    return state.sums;
  }

  @Selector()
  static sumsByParams(state: TransactionStateModel) {
    return (field: string) => state?.sums?.[field];
  }

  @Selector()
  static transactionState(state: TransactionStateModel) {
    return state;
  }
}
